/* eslint-disable no-restricted-imports */

'use client';

import React from 'react';
import { Grid, GridItem, IBreakpointName } from '../../../../ui';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import NewsCard from './NewsCard';
import { useTranslation } from '../../../shared/i18n/translate/useTranslation';
import { INewsStoryEntry } from '../../../shared/api/contentful/types/INewsStory';
import NewsSectionWrapper from './NewsSectionWrapper';
import { getMaxWidthBasedOnColumns } from '../../helpers/getMaxWidthBasedOnColumns';
import NewsCardNoImage from '../../../../../assets/icons/no-image.svg?reference';
import Responsive from '../../../../ui/utils/Responsive';

export interface INewsStoryProps {
	recentNewsStories: INewsStoryEntry[];
}

export const OurLatestStoriesSection = ({
	recentNewsStories,
}: INewsStoryProps) => {
	const { t } = useTranslation();
	const { asPath } = useRouter();

	if (!recentNewsStories || recentNewsStories.length === 0) {
		return null;
	}

	return (
		<NewsSectionWrapper
			title={t('newsroom_stories_page_title')}
			label={t('newsroom_landing_stories_label')}
			viewAllButtonLabel={t('web_view_all_news_items')}
			viewAllHref={`${asPath}stories`}
		>
			<StyledGrid>
				{recentNewsStories.map((recentNewsStory) => {
					const imageSrc =
						recentNewsStory.fields.images?.[0]?.fields?.file?.url ||
						NewsCardNoImage;

					return (
						<StyledGridItem
							columns={[3, { mobileXL: 12 }]}
							key={recentNewsStory.fields.slug}
						>
							<NewsCard
								variant="vertical"
								title={recentNewsStory.fields.title}
								summary={recentNewsStory.fields.summary}
								publicationDate={recentNewsStory.fields.publicationDate}
								linkHref={`${asPath}stories/item/${recentNewsStory.fields.slug}`}
								imageSource={imageSrc}
								category={recentNewsStory.fields.category}
							/>
						</StyledGridItem>
					);
				})}
			</StyledGrid>
		</NewsSectionWrapper>
	);
};

const StyledGrid = styled(Grid.withComponent('ul'))`
	margin-top: 0;
	margin-left: 0;
	padding-left: 0;
	list-style: none;
	gap: 24px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		gap: 16px;
		margin-bottom: 16px;
	}
`;

const StyledGridItem = styled(GridItem.withComponent('li'))<{
	columns: number | [number, Partial<{ [key in IBreakpointName]: number }>];
}>`
	display: flex;
	margin-top: 0;
	margin-left: 0;
	padding-left: 0;
	flex: 1;
	${({ columns }) => getMaxWidthBasedOnColumns(columns)};
`;
